import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Typography,
} from "@mui/material";
import { Close, InfoOutlined } from "@mui/icons-material";
import AcxButton from "components/UI/AcxButton";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import { useStore } from "utils/useStore";
import { LLMClassifierBuilderStore } from "../Stores/LLMClassifierBuilderStore";
import { LLMClassifierLibraryStore } from "../Stores/LLMClassifierLibraryStore";
import type { LLMClassifierForLibrary } from "models/LLMs";

const MaxDescriptionLength = 250;

interface UpdateFromLibraryDialogProps {
    isOpen: boolean;
    onClose: () => void;
}

export const UpdateFromLibraryDialog = observer(
    ({ isOpen, onClose }: UpdateFromLibraryDialogProps) => {
        const builderStore = useStore(LLMClassifierBuilderStore);
        const libraryStore = useStore(LLMClassifierLibraryStore);

        const [selectedLibraryModel, setSelectedLibraryModel] =
            useState<LLMClassifierForLibrary | null>(null);
        const [versionDescription, setVersionDescription] = useState("");

        const isLibraryLoading = libraryStore.getTaskLoading(
            LLMClassifierLibraryStore.Tasks.GetLLMClassifierLibrary,
        );
        const isUpdating = builderStore.getTaskLoading(
            LLMClassifierBuilderStore.Tasks.UpdateFromLibrary,
        );

        const allLibraryModels = libraryStore.getAllLibraryModels();

        // When the dialog opens, load the library and auto-select the matching model
        useEffect(() => {
            if (isOpen) {
                libraryStore.loadLibrary();
                setSelectedLibraryModel(null);
                setVersionDescription("");
            }
        }, [isOpen, libraryStore]);

        // Auto-select the matching library model by name once library loads
        useEffect(() => {
            if (
                !isLibraryLoading &&
                allLibraryModels.length > 0 &&
                !selectedLibraryModel
            ) {
                const match = allLibraryModels.find(
                    (m) => m.name === builderStore.classifier.name,
                );
                if (match) {
                    setSelectedLibraryModel(match);
                    setVersionDescription(
                        `Updated to ${match.name} model library version ${match.version}.`,
                    );
                }
            }
        }, [
            isLibraryLoading,
            allLibraryModels,
            selectedLibraryModel,
            builderStore.classifier.name,
        ]);

        const handleLibraryModelChange = (
            model: LLMClassifierForLibrary | undefined,
        ) => {
            setSelectedLibraryModel(model ?? null);
            if (model) {
                setVersionDescription(
                    `Updated to ${model.name} model library version ${model.version}.`,
                );
            } else {
                setVersionDescription("");
            }
        };

        const handleUpdate = async () => {
            if (!selectedLibraryModel || !versionDescription) return;

            await builderStore.updateFromLibrary(
                selectedLibraryModel.id,
                versionDescription,
            );
            onClose();
        };

        return (
            <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                    >
                        <Stack>
                            <Typography
                                color="gray.600"
                                fontSize="20px"
                                fontWeight="600"
                                lineHeight="32px"
                            >
                                Update from Library
                            </Typography>
                            <Typography
                                color="gray.500"
                                fontSize="13px"
                                lineHeight="20px"
                            >
                                Pull the latest version of this model from the
                                model library.
                            </Typography>
                        </Stack>
                        <IconButton onClick={onClose} size="small">
                            <Close />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <Stack gap={3}>
                        <AcxMainTextField
                            id="current-model-name"
                            labelText="Current Model"
                            fullWidth
                            value={builderStore.classifier.name ?? ""}
                            isDisabled
                        />
                        <Stack gap={1}>
                            <AcxSelectSingle<LLMClassifierForLibrary>
                                id="library-model-select"
                                inputLabel="Library Model Available"
                                valueField="id"
                                labelField="name"
                                fullWidth
                                options={allLibraryModels}
                                isLoading={isLibraryLoading}
                                defaultValue={selectedLibraryModel ?? undefined}
                                onChange={handleLibraryModelChange}
                            />
                            <Stack
                                direction="row"
                                gap={1}
                                alignItems="flex-start"
                            >
                                <InfoOutlined
                                    sx={{
                                        color: "primary.main",
                                        fontSize: "20px",
                                        mt: "1px",
                                        flexShrink: 0,
                                    }}
                                />
                                <Typography
                                    color="primary.main"
                                    fontSize="14px"
                                    lineHeight="20px"
                                >
                                    All settings will be updated to match the
                                    library except volume control, which will
                                    remain this tenant's current configuration.
                                </Typography>
                            </Stack>
                        </Stack>
                        <AcxMainTextField
                            id="update-version-description"
                            labelText="Version Description"
                            fullWidth
                            rows={3}
                            multiline
                            multilinePadding
                            maxLength={MaxDescriptionLength}
                            value={versionDescription}
                            onChange={(e) =>
                                setVersionDescription(e.target.value)
                            }
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <AcxButton color="white" onClick={onClose}>
                        Cancel
                    </AcxButton>
                    <AcxButton
                        color="primary"
                        onClick={handleUpdate}
                        loading={isUpdating}
                        disabled={!selectedLibraryModel || !versionDescription}
                    >
                        Update Model
                    </AcxButton>
                </DialogActions>
            </Dialog>
        );
    },
);
